import { useState } from 'react';
import { ImageOff } from 'lucide-react';

import { cn } from '@shared/utils/cn';
import { isPdfUrl } from '@shared/utils/media-url';

type Props = {
  url: string;
  alt: string;
  /** `thumbnail`: fills a small square, not interactive (the PDF viewer would catch the clicks). */
  variant: 'thumbnail' | 'full';
  className?: string;
};

/** An image or a PDF, with a placeholder when the file cannot be loaded (preview missing at the shop…). */
export const MediaPreview = ({ url, alt, variant, className }: Props) => {
  const [failed, setFailed] = useState(false);
  const thumbnail = variant === 'thumbnail';

  if (failed) {
    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-border bg-muted text-center text-muted-foreground',
          thumbnail ? 'size-full text-[11px]' : 'h-60 w-full text-sm',
          className
        )}
      >
        <ImageOff aria-hidden className={thumbnail ? 'size-5' : 'size-8'} />
        Visuel indisponible
      </div>
    );
  }

  if (isPdfUrl(url)) {
    return (
      <object
        aria-label={alt}
        className={cn(
          'rounded-lg border border-border bg-white',
          thumbnail ? 'pointer-events-none size-full' : 'h-[75dvh] w-full',
          className
        )}
        data={thumbnail ? `${url}#toolbar=0&navpanes=0&view=Fit` : url}
        type="application/pdf"
      />
    );
  }

  return (
    <img
      alt={alt}
      className={cn(
        'rounded-lg border border-border bg-white object-contain',
        thumbnail ? 'size-full' : 'mx-auto max-h-[75dvh] w-auto max-w-full',
        className
      )}
      decoding="async"
      loading={thumbnail ? 'lazy' : 'eager'}
      onError={() => setFailed(true)}
      src={url}
    />
  );
};
